import {
	Promotion,
	PromotionBaseProps,
} from '@/components/blocks/PromotionCard/parts/PromotionComponentMapping';
import { PROMOTION_TYPES } from '@/data/constants/promoIconConstants';
import { useSettings } from '@/data/Settings';
import { ResponseProductAttribute } from '@/data/types/Product';
import { Box } from '@mui/material';
import { useMemo } from 'react';

interface AssignedPromotionsProps {
	promoData: any;
	orderRibbons: ResponseProductAttribute[];
	partNumber?: string;
}

const hasPromoIcon = (key: string, ribbons: ResponseProductAttribute[]): boolean =>
	ribbons.some((ribbon) => ribbon.identifier === key);

export const AssignedPromotions = ({
	promoData: allPromotions,
	orderRibbons,
	partNumber,
}: AssignedPromotionsProps) => {
	const { settings } = useSettings();
	const isFetchPromoEnabled = settings.userData?.ENABLE_API_CALL_FETCH_PROMO === 'true';

	const nXm = useMemo(() => {
		if (allPromotions?.promotionData?.adminDesc === 'NxM') {
			return allPromotions.promotionData;
		}
		return null;
	}, [allPromotions]);

	const wholeSalePrice = useMemo(() => allPromotions?.savingsPackData ?? null, [allPromotions]);

	const { saveMore } = useMemo(() => {
		const result: PromotionBaseProps = {
			saveMore: null,
		};

		if (!isFetchPromoEnabled || !allPromotions) {
			return result;
		}

		if (allPromotions.promotionDataPercent) {
			result.saveMore = {
				...allPromotions.promotionDataPercent,
				tipoAhorro: 'porcentaje',
			};
		} else if (allPromotions.promotionDataFixedAmount) {
			result.saveMore = {
				...allPromotions.promotionDataFixedAmount,
				tipoAhorro: 'monto',
			};
		}

		return result;
	}, [isFetchPromoEnabled, allPromotions]);

	// --- Component Mapping & Rendering ---
	const promotionBaseProps: PromotionBaseProps = useMemo(
		() => ({
			partNumber,
			wholeSalePrice,
			saveMore,
			nXm,
		}),
		[partNumber, wholeSalePrice, saveMore, nXm]
	);

	const finalOrderRibbons = useMemo(() => {
		// Rule 1: If "Free Gift" (PROMOICON_FP) exists, exclude "N x M" (PROMORIBBON_NXM) promotions.
		if (hasPromoIcon(PROMOTION_TYPES.PROMOICON_FP, orderRibbons)) {
			return orderRibbons.filter(
				(ribbon: ResponseProductAttribute) =>
					!ribbon?.identifier?.startsWith(PROMOTION_TYPES.PROMORIBBON_NXM_)
			);
		}

		return orderRibbons;
	}, [orderRibbons]);

	// Rule 2: "Remate" (PROMOICON_GR) is exclusive and shows first but (PROMOICON_CE).
	if (hasPromoIcon(PROMOTION_TYPES.PROMOICON_GR, finalOrderRibbons)) {
		return <Promotion {...promotionBaseProps} identitifier={PROMOTION_TYPES.PROMOICON_GR} />;
	}

	return (
		<>
			{finalOrderRibbons.map((ribbon) => (
				<Promotion
					key={ribbon.identifier}
					{...promotionBaseProps}
					identitifier={ribbon.identifier as PROMOTION_TYPES}
					ContainerComponent={Box}
				/>
			))}
		</>
	);
};
